// Per-order tracking-event timeline.
//
// Reads tracking_events for a given order (canonical_type, raw_status,
// event_location, description) and renders them as a vertical timeline.
// A "Refresh" button bypasses the cron and force-polls the carrier
// for the latest scans.
//
// Drop into any admin view that has an order id:
//   <TrackingTimeline orderId={order.id} />
//
// Designed to live inside an expanded order row or a side panel. No
// state of its own beyond the query cache.
//
// Coverage model: every AP consignment is covered via the AP Shipping
// & Tracking API (the same credentials see both eParcel and MyPost
// Business). Non-AP carriers (DHL eCommerce, FedEx, etc.) fall through
// to the ShipStation v2 fallback when that plan add-on is enabled,
// and otherwise show a coverage notice instead of a blank panel. The
// envelope from /admin/tracking/orders/<id>/events carries a
// `coverage.supported` flag we render against.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import {
  Loader2, RefreshCw, MapPin, PackageCheck, Truck, Package,
  AlertTriangle, CircleDashed, HelpCircle, Info, Ban,
} from 'lucide-react'
import { getOrderTrackingEvents, refreshOrderTracking } from '../lib/api'

const CANONICAL_META = {
  picked_up:        { icon: Package,        color: 'text-slate-500',   bg: 'bg-slate-100',   label: 'Picked up' },
  in_transit:       { icon: Truck,          color: 'text-blue-600',    bg: 'bg-blue-50',     label: 'In transit' },
  at_hub:           { icon: MapPin,         color: 'text-violet-600',  bg: 'bg-violet-50',   label: 'At facility' },
  out_for_delivery: { icon: Truck,          color: 'text-amber-600',   bg: 'bg-amber-50',    label: 'Out for delivery' },
  attempted:        { icon: AlertTriangle,  color: 'text-amber-700',   bg: 'bg-amber-100',   label: 'Delivery attempted' },
  delivered:        { icon: PackageCheck,   color: 'text-emerald-600', bg: 'bg-emerald-50',  label: 'Delivered' },
  exception:        { icon: AlertTriangle,  color: 'text-red-600',     bg: 'bg-red-50',      label: 'Exception' },
  // Neutral, not red: the sender cancelled before the parcel moved, so
  // there's nothing failed here to chase.
  cancelled:        { icon: Ban,            color: 'text-slate-600',   bg: 'bg-slate-100',   label: 'Cancelled' },
  unknown:          { icon: HelpCircle,     color: 'text-slate-400',   bg: 'bg-slate-50',    label: 'Unknown' },
}

function metaFor(type) {
  return CANONICAL_META[type] || CANONICAL_META.unknown
}

function fmt(ts) {
  if (!ts) return '—'
  try {
    return format(parseISO(ts), "d MMM yyyy 'at' HH:mm")
  } catch {
    return ts
  }
}

export default function TrackingTimeline({ orderId, getEvents = getOrderTrackingEvents, full = false, canRefresh = true }) {
  const qc = useQueryClient()
  const [refreshError, setRefreshError] = useState(null)

  const { data, isLoading } = useQuery({
    // Discriminate the cache by variant so the curated Tracking-sheet view and
    // the complete order-table view never collide on the same orderId.
    queryKey: ['tracking', 'events', orderId, full ? 'full' : 'default'],
    queryFn:  () => getEvents(orderId),
    enabled:  !!orderId,
  })

  const events   = data?.events   || []
  const coverage = data?.coverage || {}
  const supported = !!coverage.supported
  // The label was voided in ShipStation and replaced. This tracking number
  // is dead — the poller skips it and a force-refresh 400s — so the generic
  // "no scans yet, check back soon" empty state would be actively misleading.
  const labelVoided = !!coverage.label_voided

  const refresh = useMutation({
    mutationFn: () => refreshOrderTracking(orderId),
    onSuccess: () => {
      setRefreshError(null)
      qc.invalidateQueries({ queryKey: ['tracking', 'events', orderId] })
    },
    onError: (err) => {
      setRefreshError(err?.response?.data?.detail || err?.message || 'Refresh failed')
    },
  })

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Truck size={14} className="text-slate-500" />
          Tracking timeline
        </div>
        {/* Hide the Refresh affordance when the carrier isn't supported
            (polling won't surface anything) OR when the caller can't refresh
            — the force-poll endpoint is admin-only, so the client portal
            passes canRefresh={false} to avoid a button that 403s on click. */}
        {supported && canRefresh && !labelVoided && (
          <button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 transition-colors disabled:opacity-50"
            title="Force-poll the carrier for the latest scans"
          >
            {refresh.isPending
              ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCw size={12} />}
            Refresh
          </button>
        )}
      </div>

      {refreshError && (
        <div className="mb-3 p-2 text-xs rounded bg-red-50 border border-red-200 text-red-700">
          {refreshError}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-6 gap-2 text-slate-400">
          <Loader2 size={14} className="animate-spin" /> <span className="text-xs">Loading…</span>
        </div>
      ) : events.length === 0 ? (
        // Three distinct empty states:
        //  1. The label was VOIDED and replaced — this number will never
        //     scan, so say that rather than implying it's still coming.
        //  2. We DO have coverage for this carrier but no events have
        //     landed yet (cold AP shipment — labelled, not picked up).
        //  3. We do NOT have coverage for this carrier. User-facing copy
        //     explains rather than implying a system failure.
        labelVoided ? (
          <div className="flex items-start gap-2 py-4 px-3 rounded bg-slate-50 border border-slate-200 text-slate-600">
            <Ban size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <p className="font-medium text-slate-700 mb-0.5">
                This label was voided and replaced
              </p>
              <p className="text-slate-500">
                The shipment was re-labelled in ShipStation, so this tracking
                number was never handed to the carrier and will never scan.
                Tracking lives on the replacement label for this order.
              </p>
            </div>
          </div>
        ) : supported ? (
          <div className="flex flex-col items-center py-6 text-slate-400">
            <CircleDashed size={20} className="mb-1" />
            <p className="text-xs">No scans yet</p>
            <p className="text-[11px] text-slate-300 mt-0.5">
              The carrier hasn't posted any checkpoint events yet — usually
              shows up within a few hours of pickup.
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-2 py-4 px-3 rounded bg-slate-50 border border-slate-200 text-slate-600">
            <Info size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <p className="font-medium text-slate-700 mb-0.5">
                Detailed tracking not enabled for this carrier yet
              </p>
              <p className="text-slate-500">
                Per-checkpoint events are wired up for Australia Post,
                StarTrack, FedEx, DHL Express and UPS. This shipment is on
                a carrier we don't poll yet, so there's nothing to show.
              </p>
            </div>
          </div>
        )
      ) : (
        <ol className="relative border-l border-slate-200 ml-2 space-y-3 pl-4">
          {events.map((ev) => {
            const m = metaFor(ev.canonical_type)
            const Icon = m.icon
            return (
              <li key={ev.id} className="relative">
                <span className={`absolute -left-[26px] flex h-5 w-5 items-center justify-center rounded-full ${m.bg}`}>
                  <Icon size={11} className={m.color} />
                </span>
                <div className="flex items-baseline justify-between gap-3">
                  <span className={`text-xs font-medium ${m.color}`}>{m.label}</span>
                  <span className="text-[11px] text-slate-400">{fmt(ev.event_time)}</span>
                </div>
                <div className="text-xs text-slate-700 mt-0.5">{ev.description || ev.raw_status}</div>
                {ev.event_location && (
                  <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                    <MapPin size={9} /> {ev.event_location}
                    {full && ev.event_postcode && (
                      <span className="text-slate-300">· {ev.event_postcode}</span>
                    )}
                  </div>
                )}
                {full && !ev.event_location && (ev.event_suburb || ev.event_state || ev.event_postcode) && (
                  <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                    <MapPin size={9} /> {[ev.event_suburb, ev.event_state, ev.event_postcode].filter(Boolean).join(' ')}
                  </div>
                )}
                {full && ev.source && (
                  <div className="text-[10px] text-slate-300 mt-0.5">via {ev.source}</div>
                )}
                {full && ev.raw_payload && (
                  <details className="mt-1">
                    <summary className="text-[10px] text-slate-300 cursor-pointer hover:text-slate-500 select-none">
                      raw payload
                    </summary>
                    <pre className="text-[10px] text-slate-500 bg-slate-50 rounded p-1.5 mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all">
                      {JSON.stringify(ev.raw_payload, null, 2)}
                    </pre>
                  </details>
                )}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}
